import { CheckOutlined, CloseOutlined, UpOutlined } from '@ant-design/icons';
import cx from 'classnames';
import AnimatedHeight from 'react-animate-height';
import { useTranslation } from 'react-i18next';
import { useToggleState } from '@app/utils';
import { Namespace } from '@data';
import { Button, AntdTypography, AntdInput, AntdSpace } from '@ui/components';
import { Filter as FilterType } from '../../../../types';
import { OptionsListLoader, OptionsListPreview } from './components';
import { OptionsList } from './components/OptionsList';

const { Text } = AntdTypography;

type FilterProps = {
  filter: FilterType;
  isFirst: boolean;
};

// Filter block component (Answer Type, Generation Type, ICD Codes...)
export const Filter = ({
  isFirst,
  filter: {
    title,
    options,
    isDynamic,
    isLoading,
    isInfinite,
    searchValue,
    placeholder,
    shouldSearch,
    canSelectAll,
    canDeselectAll,
    openByDefault,
    isFetchingNextPage,
    debouncedSearchValue,
    onChange,
    onScroll,
    onSearch,
    onSelectAll,
    getIsChecked,
    onDeselectAll,
  },
}: FilterProps) => {
  const { t } = useTranslation([Namespace.ADMIN]);
  const [isFilterOpen, toggleFilterOpen] = useToggleState(openByDefault);

  return (
    <li
      style={!isFirst ? { borderTopStyle: 'solid' } : {}}
      className={cx('py-2', !isFirst && 'border-t border-t-base-200')}
    >
      <Button type="text" className="w-full justify-start px-1 py-2" onClick={toggleFilterOpen}>
        <Text className="font-semibold text-base line-clamp-1">{t(title)}</Text>
        <UpOutlined
          className={cx('ml-auto duration-300', isFilterOpen ? 'rotate-0' : 'rotate-180')}
        />
      </Button>
      {isLoading && isFilterOpen ? (
        <OptionsListLoader />
      ) : (
        <AnimatedHeight height={isFilterOpen ? 'auto' : 0}>
          {/* top level options render */}
          {onSearch && (
            <AntdInput
              placeholder={t(placeholder ?? 'Search')}
              value={searchValue}
              onChange={(e) => onSearch(e.target.value)}
              className="w-full my-2"
            />
          )}
          {onSelectAll && onDeselectAll && (
            <AntdSpace className="w-full justify-between">
              {canSelectAll && (
                <Button
                  icon={<CheckOutlined />}
                  className="w-full justify-start text-primary-600"
                  type="text"
                  onClick={onSelectAll}
                >
                  {t('trials_library.filters.select_all')}
                </Button>
              )}
              {canDeselectAll && (
                <Button
                  icon={<CloseOutlined />}
                  type="text"
                  className="w-full justify-start text-primary-600"
                  onClick={onDeselectAll}
                >
                  {t('trials_library.filters.deselect_all')}
                </Button>
              )}
            </AntdSpace>
          )}
          <ul
            className={cx('list-none p-0 mt-1', isInfinite && 'overflow-y-auto max-h-[220px]')}
            {...(onScroll && { onScroll })}
          >
            {options.map((option) =>
              debouncedSearchValue && shouldSearch ? (
                <OptionsListPreview
                  key={option.value}
                  option={option}
                  isDynamic={isDynamic}
                  onChange={onChange}
                  getIsChecked={getIsChecked}
                />
              ) : (
                <OptionsList
                  key={option.value}
                  option={option}
                  isDynamic={isDynamic}
                  onChange={onChange}
                  getIsChecked={getIsChecked}
                />
              ),
            )}
            {isInfinite && isFetchingNextPage && <OptionsListLoader isInfinite />}
          </ul>
        </AnimatedHeight>
      )}
    </li>
  );
};
